import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import AssetsTableHeader from '@SiteComponents/AssetsTableHeader'
import AssetsTableList from './AssetsTableList'

import {
  fetchElements,
  setShouldCheckAutoFill,
  resetShouldCheckAutoFill,
  deleteElement,
} from '@SiteModules/Element/actions'

import {
  getCurrentWorkspaceName,
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import {
  getTotalCount,
  getElements,
  getCurrentPage,
  getTotalPages,
  getShouldCheckAutoFill,
  getHasLoadedInitially,
  getLoading,
} from '@SiteModules/Element/selectors'

import {
  getCurrentAccountAvailableTotalCredits,
} from '@SiteModules/Account/selectors'

import {
  IDeleteElementPayload,
} from '@SiteModules/Element/types'

import './AssetsTable.scss'

const AssetsTable: React.FC = () => {
  const dispatch = useDispatch()

  const currentWorkspaceName = useSelector(getCurrentWorkspaceName)
  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const totalCountOfElements = useSelector(getTotalCount)
  const availableTotalCredits = useSelector(getCurrentAccountAvailableTotalCredits)
  const elements = useSelector(getElements)
  const currentPage = useSelector(getCurrentPage)
  const totalPages = useSelector(getTotalPages)
  const shouldCheckAutoFill = useSelector(getShouldCheckAutoFill)
  const hasLoadedInitially = useSelector(getHasLoadedInitially)
  const loading = useSelector(getLoading)

  const hasLoadedAllAssets = hasLoadedInitially && elements.length === totalCountOfElements

  useEffect(() => {
    const scrollContainer = document.querySelector('.AssetsTableList__body')

    if (!scrollContainer) { return }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer
      const nearBottom = scrollHeight - scrollTop - clientHeight < window.Checksub.nearBottomDistance
      const hasElementsToLoad = elements.length < totalCountOfElements
      const hasPagesToLoad = currentPage < totalPages

      if (!loading && nearBottom && hasElementsToLoad && hasPagesToLoad) {
        dispatch(fetchElements({
          workspaceId: currentWorkspaceId,
          page: currentPage + 1
        }))
      }
    }

    const handleSetShouldCheckAutoFill = () => {
      dispatch(setShouldCheckAutoFill())
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleSetShouldCheckAutoFill)

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleSetShouldCheckAutoFill)
    }
  }, [currentPage, totalPages, elements, totalCountOfElements, currentWorkspaceId, loading])

  useEffect(() => {
    // Handle the case when the first page of elements does not lead to a scrollable container because it does not fill the viewport. In this case repeated fetching is needed until the container becomes scrollable
    if (shouldCheckAutoFill) {
      const scrollContainer = document.querySelector('.AssetsTableList__body')

      if (!scrollContainer || scrollContainer.scrollHeight === 0 || scrollContainer.clientHeight === 0) { return }

      const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight

      const hasElementsToLoad = elements.length < totalCountOfElements
      const hasPagesToLoad = currentPage < totalPages

      if (!loading && !isScrollable && hasElementsToLoad && hasPagesToLoad) {
        dispatch(fetchElements({
          workspaceId: currentWorkspaceId,
          page: currentPage + 1,
        }))
      } else {
        dispatch(resetShouldCheckAutoFill())
      }
    }
  }, [shouldCheckAutoFill, elements, totalCountOfElements, currentWorkspaceId, currentPage, totalPages, loading])

  const handleDeleteElement = (payload: IDeleteElementPayload) => {
    dispatch(deleteElement(payload))
  }

  return (
    <div className="AssetsTable">
      <div className="AssetsTable__main">
        <AssetsTableHeader
          currentWorkspaceName={currentWorkspaceName}
          totalCountOfElements={totalCountOfElements}
          availableTotalCredits={availableTotalCredits}
        />
        <AssetsTableList
          elements={elements}
          handleDeleteElement={handleDeleteElement}
          loading={loading}
          hasLoadedAllAssets={hasLoadedAllAssets}
        />
      </div>
    </div>
  )
}

export default AssetsTable
